import { PropsWithChildren, ReactNode, useEffect, useState } from 'react';
import {
  DocumentCard,
  DocumentCardDetails,
  ScreenWidthMinUhfMobile,
  mergeStyleSets,
  mergeStyles,
} from '@fluentui/react';
import { useId } from '@fluentui/react-hooks';
import { useThemeData } from '@msrceng/theme';

export const CollapsedPane = ({
  children,
  showAll,
  label,
  setShowAll,
}: PropsWithChildren<ICollapsedPaneProps>) => {
  const containerId = useId('CollapsedPane--container');
  const [open, setOpen] = useState(showAll);
  const { theme } = useThemeData();

  useEffect(() => {
    if (showAll !== null) {
      setOpen(showAll);
    }
  }, [showAll]);

  return (
    <details
      style={{
        height: '100%',
        width: '100%',
      }}
      open={open || false}
    >
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <summary
        className={mergeStyles({
          '&:focus-visible': {
            outline: `1px solid ${theme.palette.black}`,
            position: 'relative',
            zIndex: '1',
          },
        })}
        onClick={() => {
          if (showAll !== null && setShowAll) {
            setShowAll(null);
          }
        }}
        // TODO: A `details` element usually can be opened/closed using the keyboard, but this
        // bug in `@fluentui/react` is breaking that functionality. This `onKeyDown` prop is
        // a (hopefully) temporary workaround.
        // https://github.com/microsoft/fluentui/issues/22135
        onKeyDown={({ code }) => {
          if (code === 'Enter' || code === 'Space') {
            setOpen(previousState => !previousState);
            if (showAll !== null && setShowAll) {
              setShowAll(null);
            }
          }
        }}
      >
        {label}
      </summary>
      <DocumentCard
        id={containerId}
        styles={{
          root: { maxWidth: 'unset', height: '100%', userSelect: 'unset' },
        }}
      >
        <DocumentCardDetails
          styles={{
            root: {
              maxWidth: ScreenWidthMinUhfMobile,
              padding: '0.5em',
              whiteSpace: 'normal',
            },
          }}
        >
          {children}
        </DocumentCardDetails>
      </DocumentCard>
    </details>
  );
};

export interface ICollapsedPaneProps {
  showAll: boolean | null;
  label: string | ReactNode;
  setShowAll?: (showAll: boolean | null) => void | null;
}
